import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import {
  AUTOPILOT_LIMITS,
  STRATEGY,
  formatNative,
  nativeUnits,
  type AutopilotErrorCode,
  type AutopilotStartRequest,
  type AutopilotView,
  type SpotStartChoice,
} from "@auto-pools/core";
import { api, ApiError } from "../api/client";
import { formatAgo, formatMoney, formatSol } from "../lib/format";
import { drawdownLimitValid, entriesLimitValid } from "./autopilotLimitsValidation";
import { spotKeyStateText } from "./spot/spotKeyPresentation";

const DECIMAL = /^\d+(\.\d+)?$/;
const MIN_CAPITAL_USD = STRATEGY.sizing.minEntryUsd;
const SOL_RESERVE = formatSol(formatNative(BigInt(STRATEGY.sizing.minSolReserveLamports), 9));

const START_ERROR_TEXT: Partial<Record<AutopilotErrorCode, string>> = {
  AUTOPILOT_CAPITAL_BELOW_MINIMUM: "Starting capital must be at least $50.",
  AUTOPILOT_CAPITAL_ABOVE_AVAILABLE: "Starting capital cannot exceed the available USDC balance.",
  AUTOPILOT_INVALID_LIMITS: "Check the drawdown and entries limits.",
  AUTOPILOT_RUN_ACTIVE: "An autopilot run is already active.",
  EXECUTOR_UNAVAILABLE: "The signing wallet is unavailable.",
};

/**
 * Design 48: the rule sentence's bar and note carry the whole-mode state; the sentence itself
 * never changes color.
 */
function wholeRuleState(available: string | null): {
  tone: "pos" | "warn" | "bad";
  note: string;
} {
  if (available === null)
    return {
      tone: "bad",
      note: "The balance could not be read just now, so nothing is opened until it can be. An unreadable balance is never treated as zero.",
    };
  if (Number(available) < Number(MIN_CAPITAL_USD))
    return {
      tone: "warn",
      note: `Right now that is ${formatMoney(available)} — under the ${formatMoney(MIN_CAPITAL_USD)} minimum, so it waits and keeps watching. Nothing is stopped and it does not ask you again.`,
    };
  return {
    tone: "pos",
    note: `No amount is stored — today it would be about ${formatMoney(available)}, tomorrow whatever the wallet holds. Below ${formatMoney(MIN_CAPITAL_USD)} it waits instead of opening.`,
  };
}

/** Start is a dialog, not a toggle (design 47, section 2): capital plus exactly two operator limits. */
export function AutopilotStartDialog({
  view,
  onClose,
  onStarted,
}: {
  view: AutopilotView;
  onClose: () => void;
  onStarted: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const keep = useRef<HTMLButtonElement>(null);
  const idempotencyKey = useMemo(() => crypto.randomUUID(), []);
  // Design 48: "Use the whole free balance" is checked by default — the run holds no typed amount.
  const [useWhole, setUseWhole] = useState(true);
  const [capital, setCapital] = useState(MIN_CAPITAL_USD);
  const [drawdown, setDrawdown] = useState(view.limits.drawdownPercent);
  const [entries, setEntries] = useState(String(view.limits.entriesPer24h));
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const previous = document.activeElement,
      element = dialog.current;
    element?.showModal();
    keep.current?.focus();
    return () => {
      element?.close();
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);

  const vaultLocked = view.signer.executor !== "ready";
  const availableUsd = view.funding.available;
  const capitalValid = DECIMAL.test(capital) && Number(capital) >= Number(MIN_CAPITAL_USD);
  const capitalWithinAvailable =
    availableUsd === null || (capitalValid && Number(capital) <= Number(availableUsd));
  const drawdownValid = drawdownLimitValid(drawdown);
  const entriesValid = entriesLimitValid(entries);
  const formValid = useWhole
    ? drawdownValid && entriesValid
    : capitalValid && capitalWithinAvailable && drawdownValid && entriesValid;
  // Design 48, section 2: an unreadable balance blocks Start even though it never blocks a fixed
  // amount within bounds; a below-minimum readable balance never blocks it (it waits instead).
  const canStart =
    view.start.allowed && !vaultLocked && formValid && (!useWhole || availableUsd !== null);
  const whole = wholeRuleState(availableUsd);
  // Design 54: no manual toggle. With the spot engine on, Start always asks for spot: with a
  // usable key both engines start ("Start both"); without one the server records the request
  // (`pendingKey`, or a run that holds until the key works) and spot starts with the next daily
  // decision once a working key exists — so pools start alone now ("Start pools only"). Absent
  // `view.spot` (engine off) sends no `spot` field, which the contract reads as `skip`.
  const spotUsable = view.spot?.key.usable ?? false;
  const spotChoice: SpotStartChoice | undefined = view.spot ? "start" : undefined;
  const submitLabel = !view.spot
    ? "Start autopilot"
    : spotUsable
      ? "Start both"
      : "Start pools only";
  const keyCheckedAt = view.spot?.key.active?.lastOkAt ?? null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canStart || pending) return;
    setPending(true);
    setError(null);
    try {
      const body: AutopilotStartRequest = useWhole
        ? {
            capitalMode: "whole",
            drawdownPercent: drawdown,
            entriesPer24h: Number(entries),
            idempotencyKey,
            ...(spotChoice ? { spot: spotChoice } : {}),
          }
        : {
            capitalMode: "fixed",
            capitalNative: nativeUnits(capital, view.funding.token.decimals).toString(),
            drawdownPercent: drawdown,
            entriesPer24h: Number(entries),
            idempotencyKey,
            ...(spotChoice ? { spot: spotChoice } : {}),
          };
      await api("/autopilot/start", { method: "POST", body, sensitive: true });
      onStarted();
    } catch (reason) {
      if (reason instanceof ApiError)
        setError(START_ERROR_TEXT[reason.code as AutopilotErrorCode] ?? reason.message);
      else setError(reason instanceof Error ? reason.message : "Start was not accepted");
    } finally {
      setPending(false);
    }
  }

  return (
    <dialog
      ref={dialog}
      className="position-action-modal autopilot-dialog"
      aria-labelledby="autopilot-start-title"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      <header className="modal-header">
        <div>
          <h2 id="autopilot-start-title">Start the autopilot</h2>
          <small>Strategy r328 + C5 · picks pools, enters, watches and exits on its own</small>
        </div>
        <button type="button" className="icon-button" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </header>
      <form onSubmit={(e) => void submit(e)}>
        <div className="position-action-body autopilot-form">
          <div className="autopilot-field">
            <span>Capital (USDC)</span>
            <label className="autopilot-whole-checkbox">
              <input
                type="checkbox"
                checked={useWhole}
                onChange={(e) => {
                  setUseWhole(e.target.checked);
                }}
              />
              <span>
                Use the whole free balance
                {!useWhole && (
                  <small>
                    Nothing is fixed at start: every entry takes whatever is free at that moment, so
                    adding or withdrawing money needs no restart.
                  </small>
                )}
              </span>
            </label>
            {useWhole ? (
              <div className={`autopilot-whole-rule ${whole.tone}`}>
                <span className="autopilot-whole-rule-bar" aria-hidden="true" />
                <div className="autopilot-whole-rule-body">
                  <span className="autopilot-whole-rule-text">
                    Every entry takes whatever USDC is free at that moment
                  </span>
                  <div className="autopilot-whole-chips">
                    <span className="autopilot-whole-chip">free USDC at that moment</span>
                    <span className="autopilot-whole-chip-sep" aria-hidden="true">
                      ·
                    </span>
                    <span className="autopilot-whole-chip">{`− ${SOL_RESERVE} kept for fees`}</span>
                    <span className="autopilot-whole-chip-sep" aria-hidden="true">
                      ·
                    </span>
                    <span className="autopilot-whole-chip">{`≤ ${STRATEGY.sizing.maxLiquiditySharePercent} % of the pool's liquidity`}</span>
                  </div>
                  <small className={`autopilot-whole-rule-note ${whole.tone}`}>{whole.note}</small>
                </div>
              </div>
            ) : (
              <>
                <input
                  aria-label="Capital (USDC)"
                  inputMode="decimal"
                  value={capital}
                  onChange={(e) => {
                    setCapital(e.target.value);
                  }}
                  aria-invalid={!capitalValid || !capitalWithinAvailable}
                />
                <small>
                  At least {formatMoney(MIN_CAPITAL_USD)}
                  {availableUsd !== null && ` · up to ${formatMoney(availableUsd)} available`}.
                  Profit is reinvested, so this is a starting size, not a ceiling.
                </small>
              </>
            )}
          </div>
          <label className="autopilot-field">
            <span>Drawdown from peak (%)</span>
            <input
              inputMode="decimal"
              value={drawdown}
              onChange={(e) => {
                setDrawdown(e.target.value);
              }}
              aria-invalid={!drawdownValid}
            />
            <small>
              Stops new entries and closing duties keep running; only Resume clears it. Default{" "}
              {AUTOPILOT_LIMITS.drawdownPercent.default}%.
            </small>
          </label>
          <label className="autopilot-field">
            <span>Entries per 24 h</span>
            <input
              inputMode="numeric"
              value={entries}
              onChange={(e) => {
                setEntries(e.target.value);
              }}
              aria-invalid={!entriesValid}
            />
            <small>
              A rolling window that frees itself. Default {AUTOPILOT_LIMITS.entriesPer24h.default}.
            </small>
          </label>
          <ul className="confirmation-facts autopilot-summary">
            <li>
              Enters USDC pools that pass every r328 filter, up to {STRATEGY.maxPositions} at once —
              what is left after funding one goes to the next
            </li>
            <li>
              Exits to USDC on the 5% stops, 2 h out of range, the profit stop once armed, or{" "}
              {String(STRATEGY.exits.lowYieldExitAfterSeconds / 86_400)} days earning under{" "}
              {STRATEGY.exits.lowYieldExitPercentPerDay}%/day in fees
            </li>
            <li>No password is asked — signing uses the unlocked wallet, as manual closing does</li>
          </ul>
          {view.spot &&
            (spotUsable ? (
              <section
                className="autopilot-spot-start"
                role="group"
                aria-labelledby="autopilot-start-spot-title"
              >
                <h3 id="autopilot-start-spot-title">Spot · Bybit</h3>
                <p>
                  The whole subaccount — every USDT and coin on it is traded; its balance is read
                  when spot starts. Key checked {keyCheckedAt ? formatAgo(keyCheckedAt) : "—"}, spot
                  trading only. First decision at the next 00:00 UTC close, and all seven slices of
                  each half buy that same night; only the weekly re-pick is staggered after that.
                </p>
                <p>
                  Pool limits do not apply to spot: no drawdown limit and no entry count. Three
                  failed sales in a row raise the red state and one urgent message instead.
                </p>
                <p className="autopilot-spot-start-warning">
                  Do not trade by hand on that subaccount while it runs.
                </p>
              </section>
            ) : (
              <section
                className="autopilot-spot-start skip"
                role="group"
                aria-labelledby="autopilot-start-spot-title"
              >
                <h3 id="autopilot-start-spot-title">Spot · Bybit · will not start</h3>
                <p>
                  There is no working key, so there is nothing to start. Pools do not wait for it.
                </p>
                <p>
                  Spot is asked for with this Start: once a working Bybit key is in Settings, it
                  starts with the next daily decision. Now:{" "}
                  {spotKeyStateText(
                    view.spot.key.state,
                    view.spot.key.reason,
                    view.spot.key.active !== null,
                  ).title.toLowerCase()}
                  .
                </p>
              </section>
            ))}
          {vaultLocked && (
            <p className="inline-alert warning" role="status">
              The signing wallet is {view.signer.executor}. Unlock it in Settings before starting.
            </p>
          )}
          {!view.start.allowed && view.start.reason && (
            <p className="inline-alert warning" role="status">
              {view.start.reason}
            </p>
          )}
          {error && (
            <p className="inline-alert warning" role="alert">
              {error}
            </p>
          )}
        </div>
        <footer>
          <button ref={keep} type="button" className="button" onClick={onClose}>
            Back
          </button>
          <button className="button primary" disabled={!canStart || pending}>
            {pending ? "Starting…" : submitLabel}
          </button>
        </footer>
      </form>
    </dialog>
  );
}
